import React, { useMemo, useRef, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import {
  TARGET_FIELDS,
  TEMPLATE_CSV,
  parseCsvText,
  parseWorkbookFile,
  sheetLinkToGvizCsv,
  fetchSheetCsv,
  analyzeSource,
  transposeVertical,
  detectClientLog,
  buildImportRows,
  type ParsedSource,
  type Mapping,
  type ImportRow,
  type ClientLogDetection,
} from '../../lib/importClients';
import { pickSheetsFromDrive } from '../../lib/googlePicker';
import { aiAnalyze } from '../../lib/aiImport';
import { planHistory, type TabRole } from '../../lib/importHistory';

/* ============================================================
   ImportClients — the 3-step, card-by-card client import.
   1 Source  ·  2 Map (or quick Add / log-detect)  ·  3 Review

   The deterministic inference engine (importClients.ts) reads
   every tab, finds the real header row, sniffs column CONTENT
   (emails look like emails whatever the header says), ranks
   tabs by roster-likeness, transposes label:value profile
   sheets, and fingerprints per-client training-log workbooks.
   Unmapped columns can ride along into the coaching note, so a
   trainer's own organization scheme survives the import.
============================================================ */

type Mode = 'manual' | 'files' | 'link';
type Parse =
  | { kind: 'idle' }
  | { kind: 'parsing' }
  | { kind: 'ready'; sources: ParsedSource[] };
type ImportState =
  | { kind: 'idle' }
  | { kind: 'running'; done: number; total: number }
  | { kind: 'done'; ok: number; failed: number };
type AiStatus = { kind: 'idle' | 'running' | 'done' | 'limit' | 'unconfigured' | 'error'; note?: string };

interface ManualRow { name: string; email: string; phone: string; }
const blankManual = (): ManualRow => ({ name: '', email: '', phone: '' });

const TEMPLATE_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE_CSV)}`;

// Google's Picker sign-in runs in a popup, which iOS Safari (and most mobile
// browsers) blocks or strands on a blank accounts.google.com tab. On touch
// devices we hide Browse Drive — the native file picker reaches the Drive app
// anyway, and the share-link fetch needs no popup at all.
const DRIVE_POPUP_OK = typeof navigator === 'undefined'
  ? true
  : !(
      /iPhone|iPod|iPad|Android/i.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) // iPadOS masquerading as macOS
    );

// Prefer a transposed profile sheet when it maps better than the raw grid
// (vertical label:value tabs become one mappable row).
const bestForm = (src: ParsedSource): ParsedSource => {
  const t = transposeVertical(src);
  if (!t) return src;
  return analyzeSource(t).score > analyzeSource(src).score
    ? { ...t, tabName: t.tabName ? `${t.tabName} (profile)` : t.tabName }
    : src;
};

const sourceLabel = (s: ParsedSource, multiFile: boolean): string =>
  multiFile && s.tabName ? `${s.fileName} · ${s.tabName}` : s.tabName || s.fileName;

const ImportClients: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { clients, importClients, importHistory, showToast } = useFitStore();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [mode, setMode] = useState<Mode | null>(null);
  const [parse, setParse] = useState<Parse>({ kind: 'idle' });
  const [enabled, setEnabled] = useState<Set<number>>(new Set());
  const [mapping, setMapping] = useState<Mapping>({});
  const [noteCols, setNoteCols] = useState<Set<string>>(new Set());
  const [logHit, setLogHit] = useState<ClientLogDetection | null>(null);
  const [logName, setLogName] = useState('');
  const [aiRoles, setAiRoles] = useState<Record<number, TabRole>>({});
  const [withHistory, setWithHistory] = useState(true);
  const [histDone, setHistDone] = useState<{ sessions: number; checkIns: number; skipped: number; failed: number } | null>(null);
  const [manualRows, setManualRows] = useState<ManualRow[]>([blankManual(), blankManual(), blankManual()]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [link, setLink] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [importState, setImportState] = useState<ImportState>({ kind: 'idle' });
  const [ai, setAi] = useState<AiStatus>({ kind: 'idle' });
  const fileRef = useRef<HTMLInputElement>(null);
  const aiRun = useRef(0);          // bumps per intake — stale AI replies are dropped
  const mapTouched = useRef(false); // the coach's hand-picked mapping outranks the AI's
  const stepRef = useRef<1 | 2 | 3>(1);

  const sources = parse.kind === 'ready' ? parse.sources : [];
  const enabledSources = useMemo(() => sources.filter((_, i) => enabled.has(i)), [sources, enabled]);
  const headers = useMemo(() => [...new Set(enabledSources.flatMap((s) => s.headers))], [enabledSources]);
  const multiFile = useMemo(() => new Set(sources.map((s) => s.fileName)).size > 1, [sources]);
  const busy = importState.kind === 'running';
  stepRef.current = step;

  // What a training-log workbook would seed: pure extraction, no writes.
  const histPlan = useMemo(
    () => (logHit && mode !== 'manual' && sources.length > 0 ? planHistory(sources, aiRoles) : null),
    [logHit, mode, sources, aiRoles]
  );
  const histHasContent = !!histPlan && (histPlan.sessions.length > 0 || histPlan.checkIns.length > 0 || !!histPlan.dietNote);
  const histSummary = histPlan
    ? [
        histPlan.sessions.length > 0 && `${histPlan.sessions.length} workout${histPlan.sessions.length === 1 ? '' : 's'}`,
        histPlan.checkIns.length > 0 && `${histPlan.checkIns.length} check-in${histPlan.checkIns.length === 1 ? '' : 's'}`,
        histPlan.dietNote && 'diet protocol → note',
      ].filter(Boolean).join(' · ')
    : '';

  /* ---------- AI mapping — runs on every spreadsheet intake ---------- */
  const runAi = async (prepared: ParsedSource[]) => {
    const run = ++aiRun.current;
    setAi({ kind: 'running' });
    const res = await aiAnalyze(prepared);
    if (run !== aiRun.current) return; // a newer intake superseded this reply
    const stillMapping = stepRef.current === 2; // don't rearrange under a built Review
    if (res.kind === 'roster') {
      if (stillMapping) {
        if (res.tabs.length) setEnabled(new Set(res.tabs));
        if (!mapTouched.current && Object.keys(res.mapping).length) {
          setMapping(res.mapping);
          if (res.mapping.name) setLogHit(null); // AI overrules a false log fingerprint
        }
        if (res.noteHeaders.length) setNoteCols(new Set(res.noteHeaders));
      }
      setAi({ kind: 'done', note: res.reason || 'AI checked the mapping — review below.' });
    } else if (res.kind === 'client_log') {
      if (stillMapping) {
        setLogHit({ name: res.name, draft: res.draft });
        setLogName((prev) => (prev.trim() ? prev : res.name));
        setAiRoles(res.tabRoles);
      }
      setAi({ kind: 'done', note: res.reason || 'Looks like a single client’s training log.' });
    } else if (res.kind === 'unknown') {
      setAi({ kind: 'done', note: res.reason || 'AI couldn’t tell what this is — map it by hand below.' });
    } else if (res.kind === 'limit') {
      setAi({ kind: 'limit', note: 'Daily AI-mapping limit reached — the automatic mapping below still works.' });
    } else if (res.kind === 'unconfigured') {
      setAi({ kind: 'unconfigured' });
    } else {
      setAi({ kind: 'error', note: 'AI mapping unavailable right now — the automatic mapping below still works.' });
    }
  };

  /* ---------- shared intake: analyze, rank tabs, fingerprint logs ---------- */
  const intake = (raw: ParsedSource[], srcMode: Mode) => {
    const prepared = raw.map(bestForm).filter((s) => s.headers.length > 0);
    if (prepared.length === 0) {
      setParse({ kind: 'idle' });
      setInlineError('Nothing readable in there — no rows found.');
      return;
    }
    const log = detectClientLog(prepared, prepared[0].fileName);
    setAiRoles({});
    setWithHistory(true);
    setHistDone(null);
    // Default-enable the most roster-like tab per file; lone tabs are just on.
    const byFile = new Map<string, number>();
    prepared.forEach((s, i) => {
      const bestIdx = byFile.get(s.fileName);
      if (bestIdx === undefined || analyzeSource(s).score > analyzeSource(prepared[bestIdx]).score) {
        byFile.set(s.fileName, i);
      }
    });
    const on = new Set([...byFile.values()]);
    setMode(srcMode);
    setParse({ kind: 'ready', sources: prepared });
    setEnabled(on);
    setNoteCols(new Set());
    setLogHit(log);
    setLogName(log?.name || '');
    const primary = [...on].sort((a, b) => analyzeSource(prepared[b]).score - analyzeSource(prepared[a]).score)[0];
    setMapping(analyzeSource(prepared[primary]).mapping);
    mapTouched.current = false;
    setAi({ kind: 'idle' });
    setStep(2);
    void runAi(prepared); // heuristics above are the instant guess; AI is the real mapper
  };

  /* ---------- step 1: sources ---------- */

  const pickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setInlineError(null);
    setParse({ kind: 'parsing' });
    try {
      const all = (await Promise.all(Array.from(files).map(parseWorkbookFile))).flat();
      intake(all, 'files');
    } catch {
      setParse({ kind: 'idle' });
      setInlineError('Couldn’t read that file — is it a CSV or Excel file?');
    }
  };

  const fetchLink = async () => {
    setInlineError(null);
    const r = sheetLinkToGvizCsv(link);
    if ('error' in r) { setInlineError(r.error); return; }
    setParse({ kind: 'parsing' });
    try {
      const csv = await fetchSheetCsv(r.url);
      const { headers: h, rows: rws } = parseCsvText(csv);
      intake([{ fileName: 'Google Sheet', headers: h, rows: rws }], 'link');
    } catch (e: any) {
      setParse({ kind: 'idle' });
      setInlineError(e?.message || 'Couldn’t fetch that sheet.');
    }
  };

  const browseDrive = async () => {
    setInlineError(null);
    setParse({ kind: 'parsing' });
    try {
      const picked = await pickSheetsFromDrive();
      if (picked.length === 0) { setParse({ kind: 'idle' }); return; } // closed the picker
      intake(picked, 'link');
    } catch (e: any) {
      setParse({ kind: 'idle' });
      setInlineError(e?.message || 'Couldn’t open Google Drive.');
    }
  };

  const startManual = () => {
    setInlineError(null);
    setMode('manual');
    setLogHit(null);
    setStep(2);
  };

  /* ---------- step 2 → 3 ---------- */

  const toReview = () => {
    setInlineError(null);
    if (logHit) {
      const name = logName.trim() || logHit.name;
      const nameKey = name.toLowerCase();
      const duplicate = clients.some((c) => c.name.trim().toLowerCase() === nameKey);
      setRows([{
        key: 'log_0',
        draft: { ...logHit.draft, name },
        note: 'Imported from a training-log spreadsheet.',
        duplicate,
        included: !duplicate,
      }]);
      setSkipped(0); setTruncated(false);
    } else if (mode === 'manual') {
      const source: ParsedSource = {
        fileName: 'Typed in',
        headers: ['Name', 'Email', 'Phone'],
        rows: manualRows.filter((r) => r.name.trim()).map((r) => [r.name, r.email, r.phone]),
      };
      const res = buildImportRows([source], { name: 'Name', email: 'Email', phone: 'Phone' }, clients);
      setRows(res.rows); setSkipped(res.skipped); setTruncated(res.truncated);
    } else {
      const res = buildImportRows(enabledSources, mapping, clients, [...noteCols]);
      setRows(res.rows); setSkipped(res.skipped); setTruncated(res.truncated);
    }
    setStep(3);
  };

  /* ---------- step 3: import ---------- */

  const runImport = async (only?: ImportRow[]) => {
    const targets = only ?? rows.filter((r) => r.included);
    if (targets.length === 0) return;
    setImportState({ kind: 'running', done: 0, total: targets.length });
    const { ok, failed, ids } = await importClients(targets.map((r) => ({ draft: r.draft, note: r.note })));
    // Training-log path: the new client gets their whole history seeded too.
    if (logHit && withHistory && histPlan && histHasContent && ids[0]) {
      const hist = await importHistory(ids[0], histPlan);
      setHistDone(hist);
      if (failed === 0 && hist.failed === 0) {
        showToast(`${targets[0].draft.name} imported with ${hist.sessions} workouts`);
      }
    } else if (failed === 0) {
      showToast(`${ok} client${ok === 1 ? '' : 's'} imported`);
    }
    setImportState({ kind: 'done', ok, failed });
  };

  const toggleRow = (key: string) =>
    setRows((all) => all.map((r) => (r.key === key ? { ...r, included: !r.included } : r)));

  const toggleSourceChip = (i: number) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(i)) { if (next.size > 1) next.delete(i); }
      else next.add(i);
      return next;
    });
  };

  const includedCount = rows.filter((r) => r.included).length;
  const dupCount = rows.filter((r) => r.duplicate).length;
  const unmappedHeaders = useMemo(() => {
    const used = new Set(Object.values(mapping).filter(Boolean));
    return headers.filter((h) => !used.has(h));
  }, [headers, mapping]);

  /* ---------- shared bits ---------- */

  const stepLabels = ['Source', mode === 'manual' ? 'Add' : 'Map', 'Review'];
  const previewFor = (header?: string): string => {
    if (!header) return '';
    for (const s of enabledSources) {
      const i = s.headers.indexOf(header);
      if (i >= 0 && s.rows[0]) return s.rows[0][i] || '';
    }
    return '';
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-content" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="card flex flex-col" style={{ maxHeight: '88vh' }}>
          {/* header — title + step dots, nothing else */}
          <div className="flex items-center justify-between gap-3 mb-1">
            <h2 className="text-xl font-bold tracking-tight">Add clients</h2>
            <button onClick={() => !busy && onClose()} className="text-fit-text-secondary hover:text-white text-lg leading-none">×</button>
          </div>
          <div className="flex items-center gap-2 mb-5">
            {stepLabels.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="w-6 h-px bg-fit-line" />}
                <span className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] ${step === i + 1 ? 'text-white' : 'text-fit-muted'}`}>
                  <span className={`w-[7px] h-[7px] rounded-full ${step > i ? 'bg-white' : 'border border-fit-line-strong'}`} />
                  {label}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* body */}
          <div className="flex-1 overflow-y-auto no-scrollbar pr-1">
            {/* AI mapper status — present on both Map variants */}
            {step === 2 && mode !== 'manual' && ai.kind !== 'idle' && ai.kind !== 'unconfigured' && (
              <div className="flex items-center justify-between gap-3 rounded-fit-md border border-fit-line px-3 py-2 mb-3">
                <p className="text-[12px] text-fit-text-secondary">
                  {ai.kind === 'running' ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="w-[6px] h-[6px] rounded-full bg-white animate-pulse" />
                      AI is reading your sheet…
                    </span>
                  ) : (
                    <>✦ {ai.note}</>
                  )}
                </p>
                {(ai.kind === 'done' || ai.kind === 'error') && (
                  <button
                    onClick={() => { mapTouched.current = false; void runAi(sources); }}
                    className="text-[11px] text-fit-muted hover:text-white underline underline-offset-2 shrink-0"
                  >
                    Re-run
                  </button>
                )}
              </div>
            )}

            {/* ---------- STEP 1 ---------- */}
            {step === 1 && (
              <div className="flex flex-col gap-3">
                <button
                  onClick={startManual}
                  className="rounded-fit-md border border-fit-line hover:border-fit-line-strong hover:bg-white/5 p-4 text-left transition-colors"
                >
                  <p className="text-[14px] font-bold">Type them in</p>
                  <p className="text-[12px] text-fit-text-secondary mt-0.5">Quick rows — name, email, phone.</p>
                </button>

                {/* a real <label> (not a JS-clicked hidden input): mobile Safari and
                    Android need this to open the picker reliably, and the MIME types
                    below are what let Google Drive appear as a file source there */}
                <label
                  htmlFor="import-files"
                  className="rounded-fit-md border border-fit-line hover:border-fit-line-strong hover:bg-white/5 p-4 text-left transition-colors cursor-pointer block"
                >
                  <p className="text-[14px] font-bold">Upload files</p>
                  <p className="text-[12px] text-fit-text-secondary mt-0.5">Excel or CSV — several at once is fine. Any layout; we read every tab.</p>
                </label>
                <input
                  id="import-files"
                  ref={fileRef}
                  type="file"
                  multiple
                  accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="sr-only"
                  onChange={(e) => { pickFiles(e.target.files); e.target.value = ''; }}
                />

                <div className="rounded-fit-md border border-fit-line p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[14px] font-bold">Google Sheets</p>
                      <p className="text-[12px] text-fit-text-secondary mt-0.5">
                        {DRIVE_POPUP_OK
                          ? 'Pick sheets straight from your Drive — several at once.'
                          : 'Paste a share link — or use Upload files above, which can pull from the Drive app.'}
                      </p>
                    </div>
                    {DRIVE_POPUP_OK && (
                      <button onClick={browseDrive} disabled={parse.kind === 'parsing'} className="btn btn-primary btn-sm shrink-0">
                        Browse Drive
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--line)' }}>
                    <input
                      value={link}
                      onChange={(e) => setLink(e.target.value)}
                      placeholder={DRIVE_POPUP_OK ? '…or paste a shared sheet link' : 'Paste a shared sheet link'}
                      className="input flex-1 min-w-0"
                    />
                    <button onClick={fetchLink} disabled={!link.trim() || parse.kind === 'parsing'} className="btn btn-glass btn-sm shrink-0">
                      {parse.kind === 'parsing' ? 'Fetching…' : 'Fetch'}
                    </button>
                  </div>
                  {DRIVE_POPUP_OK && (
                    <p className="text-[11px] text-fit-muted mt-2">
                      Browse uses your Google sign-in — if your browser blocks it (cookies), paste a share link instead.
                    </p>
                  )}
                </div>

                {parse.kind === 'parsing' && <p className="text-[12px] text-fit-text-secondary">Reading…</p>}
                {inlineError && <p className="text-[12px]" style={{ color: 'var(--status-red, #ff453a)' }}>{inlineError}</p>}
                <p className="text-[11px] text-fit-muted">
                  Prefer a clean start? <a href={TEMPLATE_URL} download="fitflex-clients-template.csv" className="underline underline-offset-2 hover:text-white">Download our CSV template</a>.
                </p>
                <p className="text-[11px] text-fit-muted">
                  Any layout works — AI maps your columns from the headers and a few sample rows. The rest of your sheet never leaves your device.
                </p>
              </div>
            )}

            {/* ---------- STEP 2: training-log fingerprint ---------- */}
            {step === 2 && mode !== 'manual' && logHit && (
              <div className="flex flex-col gap-4">
                <div className="rounded-fit-md border border-fit-line-strong p-4">
                  <p className="mono-label mb-2">Client training log</p>
                  <p className="text-[13px] text-fit-text-secondary leading-relaxed">
                    We'll add this client from their log — <span className="text-white font-semibold">profile, start date, and goals</span> pulled
                    straight from the sheet{histHasContent ? ', plus their full history below' : ''}.
                  </p>
                  {histHasContent && (
                    <label className="flex items-center gap-2.5 mt-3 pt-3 cursor-pointer" style={{ borderTop: '1px solid var(--line)' }}>
                      <input
                        type="checkbox"
                        checked={withHistory}
                        onChange={() => setWithHistory((v) => !v)}
                        className="accent-white"
                      />
                      <span className="text-[13px]">
                        <span className="font-semibold">Import their full history</span>
                        <span className="text-fit-text-secondary"> — {histSummary}</span>
                      </span>
                    </label>
                  )}
                </div>
                <div>
                  <p className="mono-label mb-1.5">Client name</p>
                  <input value={logName} onChange={(e) => setLogName(e.target.value)} className="input w-full" />
                </div>
                {(logHit.draft.goals || logHit.draft.startDate) && (
                  <div className="rounded-fit-md border border-fit-line p-3 text-[12px] text-fit-text-secondary flex flex-col gap-1">
                    {logHit.draft.startDate && <p><span className="mono-label mr-2">Start</span>{logHit.draft.startDate}</p>}
                    {logHit.draft.goals && <p><span className="mono-label mr-2">Goals</span>{logHit.draft.goals}</p>}
                  </div>
                )}
                <button onClick={() => setLogHit(null)} className="self-start text-[12px] text-fit-muted hover:text-white underline underline-offset-2">
                  Not a training log? Treat it as a spreadsheet instead
                </button>
              </div>
            )}

            {/* ---------- STEP 2: map ---------- */}
            {step === 2 && mode !== 'manual' && !logHit && (
              <div className="flex flex-col gap-2.5">
                {sources.length > 1 && (
                  <div className="mb-1">
                    <p className="mono-label mb-1.5">Tabs to import</p>
                    <div className="flex flex-wrap gap-1.5">
                      {sources.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => toggleSourceChip(i)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                            enabled.has(i) ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                          }`}
                        >
                          {sourceLabel(s, multiFile)} · {s.rows.length}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {sources.length === 1 && (
                  <p className="text-[12px] text-fit-text-secondary mb-1">
                    {sourceLabel(sources[0], false)} · {sources[0].rows.length} rows
                  </p>
                )}
                {TARGET_FIELDS.map(({ field, label, required }) => (
                  <div key={field} className="grid grid-cols-[110px_1fr_minmax(0,38%)] items-center gap-3">
                    <p className="mono-label">{label}{required && ' *'}</p>
                    <select
                      value={mapping[field] || ''}
                      onChange={(e) => { mapTouched.current = true; setMapping((m) => ({ ...m, [field]: e.target.value || undefined })); }}
                      className="input"
                      style={{ padding: '7px 10px', fontSize: 13 }}
                    >
                      <option value="">— skip —</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <p className="text-[12px] text-fit-muted truncate">{previewFor(mapping[field])}</p>
                  </div>
                ))}
                {!mapping.name && <p className="text-[12px] text-fit-muted">Pick which column holds the client’s name.</p>}

                {unmappedHeaders.length > 0 && (
                  <div className="mt-2 pt-3" style={{ borderTop: '1px solid var(--line)' }}>
                    <p className="mono-label mb-1.5">Keep other columns? <span className="normal-case tracking-normal">— added to each client's note</span></p>
                    <div className="flex flex-col gap-1 overflow-y-auto no-scrollbar" style={{ maxHeight: 130 }}>
                      {unmappedHeaders.map((h) => (
                        <label key={h} className="flex items-center gap-2 text-[12px] text-fit-text-secondary cursor-pointer hover:text-white">
                          <input
                            type="checkbox"
                            checked={noteCols.has(h)}
                            onChange={() => setNoteCols((prev) => {
                              const next = new Set(prev);
                              next.has(h) ? next.delete(h) : next.add(h);
                              return next;
                            })}
                            className="accent-white"
                          />
                          <span className="truncate">{h}</span>
                          <span className="text-fit-muted truncate">{previewFor(h)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ---------- STEP 2: manual ---------- */}
            {step === 2 && mode === 'manual' && (
              <div className="flex flex-col gap-2">
                {manualRows.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1.2fr_1.2fr_0.9fr_auto] gap-2 items-center">
                    <input value={r.name} onChange={(e) => setManualRows((all) => all.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="input" autoFocus={i === 0} />
                    <input value={r.email} onChange={(e) => setManualRows((all) => all.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} placeholder="Email" className="input" />
                    <input value={r.phone} onChange={(e) => setManualRows((all) => all.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} placeholder="Phone" className="input" />
                    <button
                      onClick={() => setManualRows((all) => all.length > 1 ? all.filter((_, j) => j !== i) : all)}
                      className="text-fit-muted hover:text-white text-[13px] px-1"
                      aria-label="Remove row"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button onClick={() => setManualRows((all) => [...all, blankManual()])} className="self-start text-[12px] text-fit-text-secondary hover:text-white px-1">
                  ＋ row
                </button>
              </div>
            )}

            {/* ---------- STEP 3 ---------- */}
            {step === 3 && (
              <div className="flex flex-col gap-3">
                <p className="text-[13px] text-fit-text-secondary">
                  <span className="text-white font-semibold">{includedCount} ready</span>
                  {skipped > 0 && <> · {skipped} skipped</>}
                  {dupCount > 0 && <> · {dupCount} duplicate{dupCount === 1 ? '' : 's'}</>}
                  {truncated && <> · showing first 2000</>}
                </p>
                {logHit && withHistory && histHasContent && (
                  <p className="text-[12px] text-fit-text-secondary rounded-fit-md border border-fit-line px-3 py-2">
                    ＋ Full history: <span className="text-white">{histSummary}</span>
                  </p>
                )}
                <div className="flex flex-col rounded-fit-md border border-fit-line overflow-y-auto no-scrollbar" style={{ maxHeight: '40vh' }}>
                  {rows.map((r) => (
                    <label key={r.key} className="flex items-center gap-3 px-3 py-2 border-b border-fit-line last:border-0 cursor-pointer hover:bg-white/[0.03]">
                      <input type="checkbox" checked={r.included} onChange={() => toggleRow(r.key)} disabled={busy} className="accent-white" />
                      <span className="text-[13px] font-semibold truncate">{r.draft.name}</span>
                      <span className="text-[12px] text-fit-muted truncate flex-1">{r.draft.email}</span>
                      {r.duplicate && (
                        <span className="text-[8px] uppercase tracking-[0.08em] text-fit-text-secondary border border-fit-line-strong rounded px-1 py-0.5 leading-none shrink-0">dup</span>
                      )}
                      {r.sourceFile && <span className="text-[10px] text-fit-muted shrink-0">{r.sourceFile}</span>}
                    </label>
                  ))}
                  {rows.length === 0 && <p className="text-[13px] text-fit-muted p-4 text-center">Nothing to import.</p>}
                </div>
                {importState.kind === 'done' && (
                  <p className="text-[13px]">
                    <span className="font-semibold">{importState.ok} imported.</span>
                    {histDone && (
                      <span className="text-fit-text-secondary">
                        {' '}History: {histDone.sessions} workouts · {histDone.checkIns} check-ins
                        {histDone.skipped > 0 && <> · {histDone.skipped} already existed</>}
                        {histDone.failed > 0 && <> · {histDone.failed} failed</>}
                      </span>
                    )}
                    {importState.failed > 0 && (
                      <span className="text-fit-text-secondary"> {importState.failed} failed — <button onClick={() => runImport(rows.filter((r) => r.included))} className="underline underline-offset-2">retry</button></span>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* footer */}
          <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-fit-line">
            {step > 1 && importState.kind !== 'done' ? (
              <button onClick={() => { setStep((s) => (s === 3 ? 2 : 1) as 1 | 2); setInlineError(null); }} disabled={busy} className="btn btn-glass btn-sm">← Back</button>
            ) : <span />}
            <div className="flex items-center gap-2">
              {step === 2 && (
                <button
                  onClick={toReview}
                  disabled={logHit ? !logName.trim() : mode === 'manual' ? !manualRows.some((r) => r.name.trim()) : !mapping.name}
                  className="btn btn-primary btn-sm"
                >
                  Review →
                </button>
              )}
              {step === 3 && importState.kind !== 'done' && (
                <button onClick={() => runImport()} disabled={busy || includedCount === 0} className="btn btn-primary btn-sm">
                  {busy ? `Importing ${includedCount}…` : `Import ${includedCount}`}
                </button>
              )}
              {importState.kind === 'done' && (
                <button onClick={onClose} className="btn btn-primary btn-sm">Done</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportClients;
